import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const rates = [
  {
    name: 'Half-Day Fishing',
    price: '₱450',
    unit: 'per pax',
    desc: '4 hours of access',
    perks: ['Pond access', 'Rod & basic bait', 'Shaded waiting area', 'Locker use'],
    cta: 'Book Half-Day',
    feature: false,
  },
  {
    name: 'Full-Day Fishing',
    price: '₱750',
    unit: 'per pax',
    desc: '8 hours of access',
    perks: [
      'Pond access',
      'Rod, bait & line replenish',
      'Shaded cabana (shared)',
      'Locker use',
      'Welcome drink',
    ],
    cta: 'Book Full-Day',
    feature: true,
  },
  {
    name: 'Learn-to-Fish',
    price: '₱350',
    unit: 'per pax',
    desc: '45-min guided session',
    perks: [
      'Personal coach (kuya guide)',
      'Rod & bait included',
      'Beginner-friendly pond',
      'Photo moment',
    ],
    cta: 'Book a Lesson',
    feature: false,
  },
];

const addons = [
  ['Cabana (4 pax)', '₱500 / day'],
  ['Cabana (8 pax)', '₱900 / day'],
  ['BBQ Grill Set', '₱250'],
  ['Bait Refill', '₱80'],
  ['Catch-and-Keep', '₱220 / kg'],
  ['Photographer add-on', '₱600 / hr'],
];

export default function Rates() {
  return (
    <section id="rates" data-testid="rates-section" className="py-20 md:py-32 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="overline text-muted mb-4">Rates & inclusions</p>
            <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Transparent pricing,
              <br />
              <em className="italic font-light text-moss">no hidden fees.</em>
            </h2>
          </div>
          <p className="text-muted md:max-w-sm text-base md:text-lg">
            All rates inclusive of pond access. Add-ons are pay-as-you-go. Group rates kaya
            naming i-customize — message us.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {rates.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`rate-card-${r.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
                r.feature
                  ? 'bg-pond text-offwhite shadow-[0_24px_60px_rgba(26,59,43,0.18)]'
                  : 'bg-offwhite border border-moss/25 text-charcoal'
              }`}
            >
              {r.feature && (
                <span className="absolute -top-3 left-8 bg-sky text-pond overline px-3 py-1 rounded-full">
                  Most booked
                </span>
              )}
              <p
                className={`overline mb-3 ${
                  r.feature ? 'text-sky' : 'text-muted'
                }`}
              >
                {r.desc}
              </p>
              <h3 className="editorial-display text-3xl md:text-4xl leading-tight mb-4">
                {r.name}
              </h3>
              <div className="flex items-baseline gap-2 mb-7">
                <span className="editorial-display text-5xl md:text-6xl leading-none">
                  {r.price}
                </span>
                <span
                  className={`text-sm ${
                    r.feature ? 'text-offwhite/70' : 'text-muted'
                  }`}
                >
                  {r.unit}
                </span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {r.perks.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm md:text-base">
                    <Check
                      className={`w-4 h-4 mt-1 flex-shrink-0 ${
                        r.feature ? 'text-sky' : 'text-pond'
                      }`}
                    />
                    <span className={r.feature ? 'text-offwhite/90' : 'text-charcoal/80'}>
                      {p}
                    </span>
                  </li>
                ))}
              </ul>
              <a
                href="#book"
                data-testid={`rate-cta-${r.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`inline-flex items-center justify-center gap-2 rounded-full font-medium px-6 py-3.5 text-sm transition-all duration-300 hover:-translate-y-0.5 ${
                  r.feature
                    ? 'bg-offwhite text-pond hover:bg-sky'
                    : 'bg-pond text-offwhite hover:bg-moss hover:text-pond'
                }`}
              >
                {r.cta}
              </a>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 md:mt-20 rounded-3xl bg-offwhite border border-moss/20 p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <p className="overline text-muted mb-3">Add-ons & extras</p>
              <h3 className="editorial-display text-3xl md:text-4xl text-pond leading-tight">
                Mix & match your day.
              </h3>
            </div>
            <p className="text-muted md:max-w-sm">
              Tick lang the add-ons during booking — bayad mo on-site or in advance.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            {addons.map(([name, price]) => (
              <div
                key={name}
                className="flex items-baseline justify-between gap-4 border-b border-moss/20 pb-4"
              >
                <span className="text-charcoal text-base md:text-lg">{name}</span>
                <span className="font-display text-pond text-xl md:text-2xl">{price}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
